// The dashboard merch-rollup read — the ONE band-scoped query behind the Merch rollup
// widget (today / 30d / lifetime sales + pending-to-fulfill + best-sellers). Hand-written over the generated
// client via the tenant-keyed wrapper: keyed ['band', bandId, 'merch-rollup'] so a band switch scorches
// it AND the connection-changed / sync-completed plane-B signals invalidate it live (the Shopify/Square connect
// flip + the vendor-cache refresh). Served straight from the Redis vendor cache — a read-only rollup: the
// "Open" deep-link goes to the merchant admin (server-provided adminUrl), never an in-app checkout.
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

export type MerchCardView = components["schemas"]["MerchCardView"];
export type MerchRollup = components["schemas"]["MerchRollup"];
export type MerchBestSeller = components["schemas"]["MerchBestSeller"];

/** GET the composed merch-rollup card, gated `money:merch` since 2026-09-03 (it was `merch:read`, which is now
 * the merch OPS surface — the signup QR). `staleTime` ~60s per the stats domain. `enabled`
 * lets the caller skip the read entirely for a seat the widget is hidden from, so no
 * fetch fires for a card that never renders. */
export function useMerchRollup(options: { enabled?: boolean } = {}) {
  const { api } = useApp();
  return useBandQuery<MerchCardView>(
    ["merch-rollup"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/merch/rollup", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { staleTime: 60_000, ...options },
  );
}

export type MerchSyncQueued = components["schemas"]["MerchSyncQueued"];

/**
 * POST the manual merch sync — "Sync merch" on the Booking page's PAST tab (ART-67).
 *
 * A worker JOB (202), so like `useSyncShows` it toasts that work started and invalidates nothing
 * synchronously: the attaches land show by show and appear on the next money read.
 *
 * The toast is written from the RESULT rather than being one fixed string, because the three outcomes are
 * genuinely different situations and only one of them is "wait a moment":
 *   · no store connected → nothing will ever happen until they connect one, and the message says where;
 *   · nothing to queue   → every past show already has its merch, which is success, not a silent no-op;
 *   · queued N, M left   → the per-press cap held some back, and the user needs to know to press again.
 * A single "Sync started" would have made the first two indistinguishable from the third, which is the
 * failure mode this whole surface exists to fix — a button you press and cannot tell whether it worked.
 */
export function useSyncMerch() {
  const { api } = useApp();
  return useBandMutation<MerchSyncQueued, void>(
    async (bandId) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/merch/sync", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: (result) => {
        // The generated client types every response property as optional, so the three fields are read with
        // defaults at this boundary rather than asserted — a missing `queued` is "nothing queued", which is
        // the honest reading and keeps the copy below from saying "Syncing merch for undefined shows".
        const queued = result.queued ?? 0;
        const remaining = result.remaining ?? 0;
        if (!result.connected) {
          pushToast("No merch store connected — connect Shopify or Square in Settings → Connections.");
          return;
        }
        if (queued === 0) {
          pushToast("Every past show already has its merch sales.", "success");
          return;
        }
        const more = remaining > 0 ? ` ${remaining} more to go — sync again to continue.` : "";
        pushToast(
          `Syncing merch for ${queued} past show${queued === 1 ? "" : "s"}.${more}`,
          "success",
        );
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't start the merch sync")),
    },
  );
}
